import CurrencyExchangeOutlined from '@mui/icons-material/CurrencyExchangeOutlined';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Dialog from '@mui/material/Dialog';
import Paper from '@mui/material/Paper';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { useGetBeneficiaryVirtualAccountsQuery, useGetOwnIntegratorFeaturesQuery } from '@/api/v1';
import USFlag from '@/assets/flags/us.svg?react';
import CheckIcon from '@/assets/icons/check.svg?react';
import CloseIcon from '@/assets/icons/close.svg?react';
import DepositIcon from '@/assets/icons/deposit-icon.svg?react';
import DocumentIcon from '@/assets/icons/document.svg?react';
import ExpandIcon from '@/assets/icons/dropdown-arrow.svg?react';
import InfoIcon from '@/assets/icons/info-icon.svg?react';
import SavingsBankIcon from '@/assets/icons/savings-bank.svg?react';
import SendIcon from '@/assets/icons/send-icon.svg?react';
import SwitchIcon from '@/assets/icons/switch.svg?react';
import { EmptyState } from '@/components/atoms/EmptyState';
import { BalanceWidget } from '@/components/molecules/BalanceWidget';
import { ApplicationModal } from '@/components/organisms/ApplicationModal';
import {
  FF_DASHBOARD_NEW_ALLOW_VA_CREATION_FOR_INDIVIDUALS,
  FF_DASHBOARD_NEW_BRL1_BALANCES,
  FF_DASHBOARD_NEW_MXNB_BALANCES,
} from '@/data/featureFlags';
import { VirtualAccount } from '@/DTOs/virtualAccount';
import { useBalances } from '@/hooks/useBalances';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import { usePermissionsManager } from '@/hooks/usePermissionsManager';
import { LocaleContext, useTranslator } from '@/hooks/useTranslator';
import { CreateUsdAccountModal } from '@/Screens/Dashboard/Customers/CreateUsdAccountModal';
import { AccountDetailsModal } from '@/Screens/Dashboard/Home/AccountDetailsModal';
import { CurrencyCodeType } from '@/types/currency';
import { BeneficiaryType } from '@/types/customer';
import { CustomerKYXVerification } from '@/types/customerKYXVerification';
import { Locale } from '@/types/locale';
import { Permission } from '@/types/permissions';
import { formatAmount, FormatStyle } from '@/utility/formatAmount';
import { getAccountNickname } from '@/utility/getAccountNickname';

interface Props {
  readonly beneficiaryId: string;
  readonly beneficiaryType: CustomerKYXVerification['type'];
  readonly beneficiaryName?: string;
  readonly depositLink?: string;
  readonly transferLink?: string;
  readonly internalTransferLink?: string;
  readonly internalTransferLinkState?: unknown;
  readonly convertLink?: string;
  readonly convertLinkState?: unknown;
}

const BASE_BALANCE_CURRENCIES: readonly CurrencyCodeType[] = [
  CurrencyCodeType.USD,
  CurrencyCodeType.USDC,
  CurrencyCodeType.USDT,
];

const maskAccountNumber = (accountNumber: string | null): string => {
  if (!accountNumber) {
    return '—';
  }
  if (accountNumber.length <= 4) {
    return accountNumber;
  }
  return `····${accountNumber.slice(-4)}`;
};

export const CustomerBalanceSection: React.FC<Props> = (props: Props): React.ReactElement => {
  const {
    beneficiaryId,
    beneficiaryType,
    beneficiaryName,
    depositLink,
    transferLink,
    internalTransferLink,
    internalTransferLinkState,
    convertLink,
    convertLinkState,
  } = props;
  const tr = useTranslator();
  const locale = React.useContext<Locale>(LocaleContext);
  const { userIsAuthorizedTo } = usePermissionsManager();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMxnbBalancesEnabled = useFeatureFlag(FF_DASHBOARD_NEW_MXNB_BALANCES);
  const isBrl1BalancesEnabled = useFeatureFlag(FF_DASHBOARD_NEW_BRL1_BALANCES);
  const [selectedCurrency, setSelectedCurrency] = React.useState<CurrencyCodeType>(
    (): CurrencyCodeType => {
      const param = searchParams.get('walletCurrency');
      if (param === 'USDC') {
        return CurrencyCodeType.USDC;
      }
      if (param === 'USDT') {
        return CurrencyCodeType.USDT;
      }
      if (param === 'MXNB' && isMxnbBalancesEnabled) {
        return CurrencyCodeType.MXNB;
      }
      if (param === 'BRL1' && isBrl1BalancesEnabled) {
        return CurrencyCodeType.BRL1;
      }
      return CurrencyCodeType.USD;
    },
  );
  const [isCreateModalOpen, setIsCreateModalOpen] = React.useState(false);
  const [accountsExpanded, setAccountsExpanded] = React.useState(true);
  const [isDetailsOpen, setIsDetailsOpen] = React.useState(false);
  const [selectedVA, setSelectedVA] = React.useState<VirtualAccount | null>(null);
  const [howItWorksAnchor, setHowItWorksAnchor] = React.useState<HTMLElement | null>(null);

  const { data: virtualAccounts = [], refetch: refetchVirtualAccounts } =
    useGetBeneficiaryVirtualAccountsQuery(beneficiaryId);
  const { data: integratorFeatures, isLoading: isLoadingIntegratorFeatures } =
    useGetOwnIntegratorFeaturesQuery();
  const isVaEnabled = integratorFeatures?.vaEnabled ?? false;

  const allowVaCreationForIndividuals = useFeatureFlag(
    FF_DASHBOARD_NEW_ALLOW_VA_CREATION_FOR_INDIVIDUALS,
  );
  const canCreateUsdVirtualAccount =
    beneficiaryType === BeneficiaryType.business || allowVaCreationForIndividuals;

  const balanceCurrencies = React.useMemo(
    (): readonly CurrencyCodeType[] => [
      ...BASE_BALANCE_CURRENCIES,
      ...(isMxnbBalancesEnabled ? [CurrencyCodeType.MXNB] : []),
      ...(isBrl1BalancesEnabled ? [CurrencyCodeType.BRL1] : []),
    ],
    [isMxnbBalancesEnabled, isBrl1BalancesEnabled],
  );

  const balances = useBalances({
    currencies: balanceCurrencies,
    beneficiaryId,
  });

  const usdBalance = balances[CurrencyCodeType.USD];
  const usdcBalance = balances[CurrencyCodeType.USDC];
  const usdtBalance = balances[CurrencyCodeType.USDT];
  const mxnbBalance = balances[CurrencyCodeType.MXNB];
  const brl1Balance = balances[CurrencyCodeType.BRL1];

  const canViewUsdAccount = userIsAuthorizedTo(Permission.VIEW_USD_ACCOUNT);
  const canCreateVa =
    canCreateUsdVirtualAccount && userIsAuthorizedTo(Permission.CREATE_USD_ACCOUNT);
  const hasVirtualAccounts = virtualAccounts.length > 0;

  const onToggleAccounts = React.useCallback((_: React.SyntheticEvent, expanded: boolean): void => {
    setAccountsExpanded(expanded);
  }, []);

  const onOpenCreateModal = React.useCallback((): void => {
    setIsCreateModalOpen(true);
  }, []);

  const onCloseCreateModal = React.useCallback((): void => {
    setIsCreateModalOpen(false);
  }, []);

  const onShowVADetails = React.useCallback((va: VirtualAccount): void => {
    setSelectedVA(va);
    setIsDetailsOpen(true);
  }, []);

  const onCloseDetails = React.useCallback((): void => {
    setIsDetailsOpen(false);
    setSelectedVA(null);
  }, []);

  const onOpenHowItWorks = React.useCallback((event: React.MouseEvent<HTMLElement>): void => {
    setHowItWorksAnchor(event.currentTarget);
  }, []);

  const onCloseHowItWorks = React.useCallback((): void => {
    setHowItWorksAnchor(null);
  }, []);

  return (
    <>
      <Paper elevation={3} sx={styles.paper}>
        <Stack gap={3}>
          {/* Header */}
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">{tr('CUSTOMER_ACCOUNT_BALANCES')}</Typography>
            {userIsAuthorizedTo(Permission.CREATE_TRANSACTIONS) && (
              <Stack direction="row" gap={1.5}>
                {depositLink && (
                  <ButtonBase component={Link} to={depositLink} sx={styles.actionButton}>
                    <Box sx={styles.actionButtonIcon}>
                      <DepositIcon style={actionIconStyle} stroke="currentColor" />
                    </Box>
                    <Typography variant="h8">{tr('ADD_FUNDS')}</Typography>
                  </ButtonBase>
                )}
                {transferLink && (
                  <ButtonBase component={Link} to={transferLink} sx={styles.actionButton}>
                    <Box sx={styles.actionButtonIcon}>
                      <SendIcon style={actionIconStyle} stroke="currentColor" />
                    </Box>
                    <Typography variant="h8">{tr('TRANSFER')}</Typography>
                  </ButtonBase>
                )}
                {internalTransferLink && (
                  <ButtonBase
                    component={Link}
                    to={internalTransferLink}
                    state={internalTransferLinkState}
                    sx={styles.actionButton}>
                    <Box sx={styles.actionButtonIcon}>
                      <SwitchIcon style={actionIconStyle} stroke="currentColor" />
                    </Box>
                    <Typography variant="h8">{tr('INTERNAL_TRANSFER_ACTION')}</Typography>
                  </ButtonBase>
                )}
                {convertLink && (
                  <ButtonBase
                    component={Link}
                    to={convertLink}
                    state={convertLinkState}
                    sx={styles.actionButton}>
                    <Box sx={styles.actionButtonIcon}>
                      <CurrencyExchangeOutlined sx={{ fontSize: 14 }} />
                    </Box>
                    <Typography variant="h8">{tr('CONVERT')}</Typography>
                  </ButtonBase>
                )}
              </Stack>
            )}
          </Stack>

          {/* Currency toggle */}
          <Tabs
            value={selectedCurrency}
            onChange={(_: React.SyntheticEvent, value: CurrencyCodeType): void => {
              setSelectedCurrency(value);
              setSearchParams(
                (prev: URLSearchParams): URLSearchParams => {
                  const next = new URLSearchParams(prev);
                  if (value === CurrencyCodeType.USD) {
                    next.delete('walletCurrency');
                  } else {
                    next.set('walletCurrency', value);
                  }
                  return next;
                },
                { replace: true },
              );
            }}
            sx={styles.tabs}>
            <Tab label="USD" value={CurrencyCodeType.USD} />
            <Tab label="USDC" value={CurrencyCodeType.USDC} />
            <Tab label="USDT" value={CurrencyCodeType.USDT} />
            {isMxnbBalancesEnabled && <Tab key="MXNB" label="MXNB" value={CurrencyCodeType.MXNB} />}
            {isBrl1BalancesEnabled && <Tab key="BRL1" label="BRL1" value={CurrencyCodeType.BRL1} />}
          </Tabs>

          {/* USD tab content */}
          {selectedCurrency === CurrencyCodeType.USD && (
            <>
              {/* USD Balance card */}
              {canViewUsdAccount && usdBalance && (
                <Box sx={styles.cardContainer}>
                  <Box display="flex" alignItems="center" gap={3}>
                    <USFlag width={32} height="100%" />
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      <Typography variant="h7" color="custom.LightGray">
                        {tr('USD_BALANCE')}
                      </Typography>
                      <Typography
                        fontSize={24}
                        fontWeight={400}
                        lineHeight="120%"
                        letterSpacing="-1%">
                        {formatAmount(
                          locale,
                          usdBalance.available,
                          CurrencyCodeType.USD,
                          FormatStyle.short,
                        )}
                      </Typography>
                    </Box>
                  </Box>

                  <Stack gap={0.5}>
                    <Typography variant="body2" fontWeight={600}>
                      {tr('USD_BALANCE_HEADLINE')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {tr('USD_BALANCE_HELPER')}
                    </Typography>
                  </Stack>
                  <ButtonBase disableRipple onClick={onOpenHowItWorks} sx={styles.howItWorksLink}>
                    <Typography variant="caption" color="primary.main">
                      {tr('HOW_THIS_WORKS')}
                    </Typography>
                  </ButtonBase>
                </Box>
              )}

              {/* US Account Numbers card */}
              {canViewUsdAccount && (
                <Accordion
                  expanded={accountsExpanded}
                  onChange={onToggleAccounts}
                  disableGutters
                  elevation={0}
                  sx={styles.accordion}>
                  <AccordionSummary
                    expandIcon={<ExpandIcon style={expandIconStyle} />}
                    sx={styles.accordionSummary}>
                    <Stack direction="row" alignItems="center" gap={1.5} flex={1}>
                      <Box sx={styles.iconCircle}>
                        <SavingsBankIcon width={20} height={20} />
                      </Box>
                      <Typography variant="h7">{tr('USD_ACCOUNTS')}</Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={styles.accordionDetails}>
                    <Stack gap={2}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Stack gap={0.5}>
                          <Typography variant="body2" fontWeight={600}>
                            {tr('VA_ACCOUNTS_HEADLINE')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {tr('VA_ACCOUNTS_DESCRIPTION')}
                          </Typography>
                        </Stack>
                        {canCreateVa && (
                          <Tooltip
                            title={!isVaEnabled ? tr('CREATE_USD_ACCOUNT_DISABLED_TOOLTIP') : ''}
                            placement="top">
                            <Box component="span">
                              <Button
                                variant="contained"
                                size="compact"
                                disabled={!isVaEnabled}
                                endIcon={
                                  !isVaEnabled ? (
                                    <InfoIcon fill="currentColor" width={16} height={16} />
                                  ) : undefined
                                }
                                loading={isLoadingIntegratorFeatures}
                                loadingPosition="start"
                                onClick={onOpenCreateModal}>
                                {tr('ADD_ACCOUNT')}
                              </Button>
                            </Box>
                          </Tooltip>
                        )}
                      </Stack>

                      {hasVirtualAccounts && (
                        <>
                          <Table size="small" sx={styles.table}>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ ...styles.headerCell, width: '40%' }}>
                                  {tr('ACCOUNT_NAME')}
                                </TableCell>
                                <TableCell sx={{ ...styles.headerCell, width: '30%' }}>
                                  {tr('ACCOUNT_NUMBER')}
                                </TableCell>
                                <TableCell sx={{ ...styles.headerCell, width: '30%' }}>
                                  {tr('ACTIONS')}
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {virtualAccounts.map(
                                (va: VirtualAccount): React.ReactElement => (
                                  <TableRow key={va.accountNumber}>
                                    <TableCell sx={styles.bodyCell}>
                                      <Typography variant="body2" fontWeight={500} noWrap>
                                        {getAccountNickname(va, beneficiaryName)}
                                      </Typography>
                                    </TableCell>
                                    <TableCell sx={styles.bodyCell}>
                                      <Typography variant="body2" color="text.secondary">
                                        {maskAccountNumber(va.accountNumber)}
                                      </Typography>
                                    </TableCell>
                                    <TableCell sx={styles.bodyCell}>
                                      <ButtonBase
                                        onClick={(): void => onShowVADetails(va)}
                                        sx={styles.detailsLink}>
                                        <Typography
                                          variant="body2"
                                          color="primary.main"
                                          fontWeight={500}>
                                          {tr('ACCOUNT_DETAILS')}
                                        </Typography>
                                        <DocumentIcon width={16} height={16} fill="currentColor" />
                                      </ButtonBase>
                                    </TableCell>
                                  </TableRow>
                                ),
                              )}
                            </TableBody>
                          </Table>

                          <Stack direction="row" alignItems="center" gap={0.5}>
                            <Typography variant="caption" color="text.secondary">
                              {'ℹ'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {tr('VA_DEPOSITS_FOOTER')}
                            </Typography>
                          </Stack>
                        </>
                      )}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              )}
            </>
          )}

          {/* USDC tab content */}
          {selectedCurrency === CurrencyCodeType.USDC && (
            <BalanceWidget
              balance={
                usdcBalance ?? {
                  available: 0,
                  pending: 0,
                  total: 0,
                  currencyCode: CurrencyCodeType.USDC,
                }
              }
              showDetailsButton
              beneficiaryId={beneficiaryId}
              label={tr('USDC_WALLET')}
              variant="outlined"
              showNativeIcon
            />
          )}

          {/* USDT tab content */}
          {selectedCurrency === CurrencyCodeType.USDT && (
            <BalanceWidget
              balance={
                usdtBalance ?? {
                  available: 0,
                  pending: 0,
                  total: 0,
                  currencyCode: CurrencyCodeType.USDT,
                }
              }
              showDetailsButton
              beneficiaryId={beneficiaryId}
              label={tr('USDT_WALLET')}
              variant="outlined"
              showNativeIcon
            />
          )}

          {/* MXNB tab content */}
          {isMxnbBalancesEnabled && selectedCurrency === CurrencyCodeType.MXNB && (
            <BalanceWidget
              balance={
                mxnbBalance ?? {
                  available: 0,
                  pending: 0,
                  total: 0,
                  currencyCode: CurrencyCodeType.MXNB,
                }
              }
              showDetailsButton
              beneficiaryId={beneficiaryId}
              label={tr('MXNB_WALLET')}
              variant="outlined"
              showNativeIcon
            />
          )}

          {/* BRL1 tab content */}
          {isBrl1BalancesEnabled && selectedCurrency === CurrencyCodeType.BRL1 && (
            <BalanceWidget
              balance={
                brl1Balance ?? {
                  available: 0,
                  pending: 0,
                  total: 0,
                  currencyCode: CurrencyCodeType.BRL1,
                }
              }
              showDetailsButton={false}
              beneficiaryId={beneficiaryId}
              label={tr('BRL1_WALLET')}
              variant="outlined"
              showNativeIcon
            />
          )}

          {!usdBalance &&
            !usdcBalance &&
            !usdtBalance &&
            (!isMxnbBalancesEnabled || !mxnbBalance) &&
            (!isBrl1BalancesEnabled || !brl1Balance) && (
              <EmptyState.Container sx={styles.emptyStateContainer}>
                <EmptyState.Title variant="h7">{tr('NO_BALANCES_YET')}</EmptyState.Title>
              </EmptyState.Container>
            )}
        </Stack>
      </Paper>

      <Popover
        open={Boolean(howItWorksAnchor)}
        anchorEl={howItWorksAnchor}
        onClose={onCloseHowItWorks}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
        <Box sx={styles.popoverContent}>
          <Stack gap={3}>
            <Stack gap={1.5}>
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography variant="body2" fontWeight={600}>
                  {tr('HOW_IT_WORKS_USD_BALANCE_TITLE')}
                </Typography>
                <Box sx={styles.popoverBadgeDefault}>{tr('DEFAULT')}</Box>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {tr('HOW_IT_WORKS_USD_BALANCE_DESC')}
              </Typography>
              <Stack gap={0.5}>
                <Stack direction="row" gap={1} alignItems="flex-start">
                  <Box sx={{ flexShrink: 0, mt: '2px', color: 'primary.main', display: 'flex' }}>
                    <CheckIcon width={14} height={14} />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {tr('HOW_IT_WORKS_USD_BALANCE_1')}
                  </Typography>
                </Stack>
                <Stack direction="row" gap={1} alignItems="flex-start">
                  <Box sx={{ flexShrink: 0, mt: '2px', color: 'primary.main', display: 'flex' }}>
                    <CheckIcon width={14} height={14} />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {tr('HOW_IT_WORKS_USD_BALANCE_2')}
                  </Typography>
                </Stack>
                <Stack direction="row" gap={1} alignItems="flex-start">
                  <Box sx={{ flexShrink: 0, mt: '2px', color: 'text.disabled', display: 'flex' }}>
                    <CloseIcon width={14} height={14} fill="currentColor" />
                  </Box>
                  <Typography variant="caption" color="text.disabled">
                    {tr('HOW_IT_WORKS_USD_BALANCE_3')}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
            <Box sx={{ borderTop: 1, borderColor: 'custom.DividerLight' }} />
            <Stack gap={1.5}>
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography variant="body2" fontWeight={600}>
                  {tr('HOW_IT_WORKS_USD_ACCOUNTS_TITLE')}
                </Typography>
                <Box sx={styles.popoverBadgeOptional}>{tr('OPTIONAL')}</Box>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {tr('HOW_IT_WORKS_USD_ACCOUNTS_DESC')}
              </Typography>
              <Stack gap={0.5}>
                <Stack direction="row" gap={1} alignItems="flex-start">
                  <Box sx={{ flexShrink: 0, mt: '2px', color: 'primary.main', display: 'flex' }}>
                    <CheckIcon width={14} height={14} />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {tr('HOW_IT_WORKS_USD_ACCOUNTS_1')}
                  </Typography>
                </Stack>
                <Stack direction="row" gap={1} alignItems="flex-start">
                  <Box sx={{ flexShrink: 0, mt: '2px', color: 'primary.main', display: 'flex' }}>
                    <CheckIcon width={14} height={14} />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {tr('HOW_IT_WORKS_USD_ACCOUNTS_2')}
                  </Typography>
                </Stack>
                <Stack direction="row" gap={1} alignItems="flex-start">
                  <Box sx={{ flexShrink: 0, mt: '2px', color: 'primary.main', display: 'flex' }}>
                    <CheckIcon width={14} height={14} />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {tr('HOW_IT_WORKS_USD_ACCOUNTS_3')}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </Box>
      </Popover>

      <CreateUsdAccountModal
        open={isCreateModalOpen}
        beneficiaryId={beneficiaryId}
        existingAccountCount={virtualAccounts.length}
        onClose={onCloseCreateModal}
        onSuccess={refetchVirtualAccounts}
      />

      <Dialog open={isDetailsOpen} onClose={onCloseDetails}>
        <ApplicationModal
          title={tr('ACCOUNT_DETAILS')}
          description={tr('USD_ACCOUNT_DESCRIPTION')}
          onClose={onCloseDetails}>
          <AccountDetailsModal
            currency={CurrencyCodeType.USD}
            beneficiaryId={beneficiaryId}
            virtualAccountId={selectedVA?.accountNumber ?? undefined}
          />
        </ApplicationModal>
      </Dialog>
    </>
  );
};

const actionIconStyle = {
  width: 12,
  height: 12,
};

const expandIconStyle = {
  width: 16,
  height: 16,
  transition: 'transform 0.2s ease',
};

const styles: Record<string, SystemStyleObject> = {
  paper: {
    p: 5,
    borderRadius: 2,
  },
  tabs: {
    '& .MuiTab-root': {
      textTransform: 'none',
      fontSize: '1rem',
      fontWeight: 500,
      minWidth: 120,
      color: 'custom.LightGray',
      '&.Mui-selected': {
        color: 'text.primary',
      },
    },
    '& .MuiTabs-indicator': {
      height: '2px',
      backgroundColor: 'text.primary',
    },
  },
  cardContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'custom.DividerLight',
    borderRadius: 2,
    px: 3,
    py: 2.5,
  },
  howItWorksLink: {
    display: 'inline-flex',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 0.5,
    color: 'primary.main',
  },
  popoverContent: {
    maxWidth: 380,
    p: 3,
  },
  popoverBadgeDefault: {
    fontFamily: 'Lexend, sans-serif',
    fontSize: 11,
    fontWeight: 500,
    lineHeight: 1,
    px: 1,
    py: 0.5,
    borderRadius: 1,
    backgroundColor: 'custom.Primary1Background',
    color: 'primary.main',
  },
  popoverBadgeOptional: {
    fontFamily: 'Lexend, sans-serif',
    fontSize: 11,
    fontWeight: 500,
    lineHeight: 1,
    px: 1,
    py: 0.5,
    borderRadius: 1,
    backgroundColor: 'custom.InputsBackground',
    color: 'text.secondary',
  },
  iconCircle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 48,
    height: 48,
    borderRadius: '50%',
    backgroundColor: 'custom.InputsBackground',
    color: 'text.primary',
    flexShrink: 0,
  },
  accordion: {
    '&::before': {
      display: 'none',
    },
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'custom.DividerLight',
    borderRadius: '8px !important',
    overflow: 'hidden',
  },
  accordionSummary: {
    px: 3,
    py: 1,
    '& .MuiAccordionSummary-content': {
      my: 1,
    },
  },
  accordionDetails: {
    px: 3,
    pb: 3,
    pt: 0,
  },
  table: {
    '& .MuiTableCell-root': {
      borderColor: 'custom.DividerLight',
    },
  },
  headerCell: {
    py: 1,
    color: 'text.primary',
    fontWeight: 400,
    borderBottom: 1,
    borderColor: 'custom.DividerLight',
  },
  bodyCell: {
    py: 1.5,
  },
  detailsLink: {
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
    py: 0.5,
    color: 'primary.main',
  },
  emptyStateContainer: {
    flex: 1,
    minHeight: 200,
    backgroundColor: 'custom.OffWhite',
    borderRadius: 2,
    px: 7,
  },
  actionButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    height: 48,
    px: 4,
    minWidth: 140,
    backgroundColor: 'custom.InputsBackground',
    borderRadius: 4,
  },
  actionButtonIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    borderRadius: 4,
    backgroundColor: 'custom.White',
    color: 'text.primary',
  },
};
